<template>
  <div class="page-container">

    <CrmPageHeader
      title="Importación de Datos"
      subtitle="Seleccione el tipo de datos que desea importar"
      icon="fas fa-file-upload"
    >
      <router-link to="/importaciones/reportes" class="btn btn-sm btn-outline-primary d-flex align-items-center gap-1">
        <i class="fas fa-chart-line"></i>
        Ver Reportes
      </router-link>
    </CrmPageHeader>

    <!-- Upload progress bar -->
    <div v-if="uploadProgress > 0 && uploadProgress < 100" class="upload-progress-wrap mb-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <span class="upload-progress-label"><i class="fas fa-spinner fa-spin me-2"></i>Subiendo archivo...</span>
        <span class="upload-progress-pct">{{ uploadProgress }}%</span>
      </div>
      <div class="upload-progress-track">
        <div class="upload-progress-fill" :style="`width:${uploadProgress}%`"></div>
      </div>
    </div>

    <!-- Portal cards -->
    <div class="import-grid">
      <div
        v-for="portal in portales"
        :key="portal.type"
        class="import-card"
      >
        <div class="import-card__top">
          <div class="import-card__icon" :style="{ background: portal.color }">
            <i :class="portal.icon"></i>
          </div>
          <div class="import-card__info">
            <p class="import-card__title">{{ portal.title }}</p>
            <p class="import-card__desc">{{ portal.description }}</p>
          </div>
        </div>
        <div class="import-card__actions">
          <button
            class="import-btn import-btn--outline"
            @click="descargarPlantilla(portal)"
            :disabled="loading"
          >
            <i class="fas fa-download"></i>
            Plantilla
          </button>
          <button
            class="import-btn import-btn--primary"
            @click="abrirModalImportar(portal)"
            :disabled="loading"
          >
            <i class="fas fa-file-upload"></i>
            Importar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Importación -->
    <SoftModal
      v-if="portalSeleccionado && showImportModal"
      :modalActive="showImportModal"
      @close="cerrarModalImportar"
      size="md"
    >
      <template #header>
        <div style="display:flex;align-items:center;gap:10px">
          <div :style="`width:32px;height:32px;border-radius:8px;background:${portalSeleccionado.color};display:flex;align-items:center;justify-content:center;color:#fff;font-size:.85rem`">
            <i :class="portalSeleccionado.icon"></i>
          </div>
          <span>Importar {{ portalSeleccionado.title }}</span>
        </div>
      </template>

      <div class="modal-import-body">
        <div class="modal-tip">
          <i class="fas fa-info-circle"></i>
          <span>Asegúrese de usar la plantilla correcta antes de importar.</span>
        </div>

        <button
          class="import-btn import-btn--outline w-100"
          @click="descargarPlantilla(portalSeleccionado)"
        >
          <i class="fas fa-download"></i>
          Descargar Plantilla
        </button>

        <div>
          <label class="upload-label">Seleccionar archivo Excel</label>
          <div
            class="upload-dropzone"
            :class="{ 'upload-dropzone--drag': isDragging, 'upload-dropzone--filled': archivoSeleccionado }"
            @click="$refs.fileInput.click()"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleFileDrop"
          >
            <input
              type="file"
              ref="fileInput"
              @change="handleFileSelect"
              accept=".xlsx,.xls"
              class="d-none"
            />

            <div v-if="!archivoSeleccionado" class="upload-dropzone__empty">
              <div class="upload-dropzone__icon">
                <i class="fas fa-cloud-upload-alt"></i>
              </div>
              <p class="upload-dropzone__text">Haga clic o arrastre el archivo aquí</p>
              <p class="upload-dropzone__hint">Formatos: .xlsx, .xls · Máx. 10MB</p>
            </div>

            <div v-else class="upload-dropzone__file">
              <div class="upload-file-icon">
                <i class="fas fa-file-excel"></i>
              </div>
              <div class="upload-file-info">
                <p class="upload-file-name">{{ archivoSeleccionado.name }}</p>
                <p class="upload-file-size">{{ formatFileSize(archivoSeleccionado.size) }}</p>
              </div>
              <button class="upload-file-remove" @click.stop="limpiarArchivo" :disabled="loading">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>

        <div v-if="errorValidacion" class="modal-error">
          <i class="fas fa-exclamation-triangle me-2"></i>{{ errorValidacion }}
        </div>

        <!-- La importacion es sincrona: tras subir, el servidor sigue procesando -->
        <div v-if="loading" class="modal-tip">
          <i class="fas fa-spinner fa-spin"></i>
          <span v-if="uploadProgress < 100">Subiendo archivo… {{ uploadProgress }}%</span>
          <span v-else>Procesando el archivo. Con archivos grandes puede tardar varios segundos; no cierres esta ventana.</span>
        </div>
      </div>

      <template #footer>
        <div class="d-flex gap-2 justify-content-end">
          <button class="import-btn import-btn--outline" @click="cerrarModalImportar" :disabled="loading">
            Cancelar
          </button>
          <button
            class="import-btn import-btn--primary"
            @click="procesarImportacion"
            :disabled="!archivoSeleccionado || loading"
          >
            <span v-if="loading"><i class="fas fa-spinner fa-spin me-1"></i>Procesando...</span>
            <span v-else><i class="fas fa-upload me-1"></i>Importar Datos</span>
          </button>
        </div>
      </template>
    </SoftModal>

    <!-- Modal de Resultados -->
    <SoftModal
      v-if="resultadoImportacion && showResultModal"
      :modalActive="showResultModal"
      @close="cerrarModalResultado"
      size="lg"
    >
      <template #header>
        <div style="display:flex;align-items:center;gap:10px">
          <div :style="`width:32px;height:32px;border-radius:8px;background:${ESTADO_RESULTADO[estadoResultado].color};display:flex;align-items:center;justify-content:center;color:#fff;font-size:.85rem`">
            <i :class="ESTADO_RESULTADO[estadoResultado].icon"></i>
          </div>
          <span>{{ ESTADO_RESULTADO[estadoResultado].titulo }}</span>
        </div>
      </template>

      <div>
        <div class="result-alert" :class="`result-alert--${ESTADO_RESULTADO[estadoResultado].alerta}`">
          {{ resultadoImportacion.message }}
          <template v-if="estadoResultado === 'parcial'">
            <br />
            <strong>{{ resultadoImportacion.data.filas_fallidas }} fila(s) no se importaron.</strong>
            Revisa los errores abajo, corrígelas en el Excel y vuelve a subirlas.
          </template>
        </div>

        <div v-if="resultadoImportacion.success && resultadoImportacion.data" class="mt-4">
          <div class="result-stats">
            <div class="result-stat">
              <i class="fas fa-list result-stat__icon" style="color:#2563EB"></i>
              <p class="result-stat__value">{{ resultadoImportacion.data.total_filas }}</p>
              <p class="result-stat__label">Total Filas</p>
            </div>
            <div class="result-stat result-stat--success">
              <i class="fas fa-check result-stat__icon"></i>
              <p class="result-stat__value">{{ resultadoImportacion.data.filas_exitosas }}</p>
              <p class="result-stat__label">Exitosas</p>
            </div>
            <div class="result-stat result-stat--error">
              <i class="fas fa-times result-stat__icon"></i>
              <p class="result-stat__value">{{ resultadoImportacion.data.filas_fallidas }}</p>
              <p class="result-stat__label">Fallidas</p>
            </div>
          </div>

          <div class="result-progress mt-3">
            <div class="d-flex justify-content-between mb-1">
              <span style="font-size:.78rem;color:#64748B">Tasa de Éxito</span>
              <span style="font-size:.78rem;font-weight:700;color:#1E293B">{{ resultadoImportacion.data.tasa_exito }}%</span>
            </div>
            <div class="result-progress-track">
              <div
                class="result-progress-fill"
                :class="resultadoImportacion.data.tasa_exito >= 80 ? 'result-progress-fill--success' : 'result-progress-fill--warn'"
                :style="`width:${resultadoImportacion.data.tasa_exito}%`"
              ></div>
            </div>
          </div>

          <!-- Detalle opcional: cada portal trae campos distintos -->
          <div v-if="estadisticasVisibles.length" class="mt-4">
            <p class="section-mini-label">Detalle</p>
            <div class="stats-grid">
              <div v-for="stat in estadisticasVisibles" :key="stat.campo" class="stats-grid__item">
                <span class="stats-grid__label">{{ stat.label }}</span>
                <span class="stats-grid__value">{{ stat.valor }}</span>
              </div>
            </div>
          </div>

          <div v-if="detallePorPortal.length" class="mt-4">
            <p class="section-mini-label">Por portal</p>
            <div class="table-responsive result-table">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Portal</th>
                    <th class="text-end">Procesados</th>
                    <th class="text-end">Exitosos</th>
                    <th class="text-end">Fallidos</th>
                    <th v-if="detalleConExistentes" class="text-end">Ya existían</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in detallePorPortal" :key="p.slug">
                    <td>{{ p.nombre }}</td>
                    <td class="text-end">{{ p.procesados }}</td>
                    <td class="text-end text-success">{{ p.exitosos }}</td>
                    <td class="text-end" :class="{ 'text-danger': p.fallidos }">{{ p.fallidos }}</td>
                    <td v-if="detalleConExistentes" class="text-end">{{ p.existentes ?? 0 }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="erroresResultado.length" class="mt-4">
            <p class="section-mini-label">Errores encontrados ({{ totalErrores }})</p>
            <p v-if="totalErrores > erroresResultado.length" class="errores-truncados">
              <i class="fas fa-info-circle me-1"></i>
              Se muestran los primeros {{ erroresResultado.length }} de {{ totalErrores }} errores.
            </p>
            <div class="table-responsive result-table" style="max-height:260px;overflow-y:auto">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Referencia</th>
                    <th v-if="erroresConPortal">Portal</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(error, index) in erroresResultado" :key="index">
                    <td>{{ error.fila }}</td>
                    <td>{{ error.referencia || error.codigo || '—' }}</td>
                    <td v-if="erroresConPortal">{{ error.portal ? nombrePortal(error.portal) : '—' }}</td>
                    <td style="color:#DC2626">{{ error.error }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="d-flex gap-2 justify-content-end">
          <button class="import-btn import-btn--outline" @click="cerrarModalResultado">Cerrar</button>
          <router-link to="/importaciones/reportes" class="import-btn import-btn--primary" @click="cerrarModalResultado">
            <i class="fas fa-chart-line me-1"></i>Ver Reportes
          </router-link>
        </div>
      </template>
    </SoftModal>

  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import CrmPageHeader from '@/components/CrmPageHeader.vue';
import SoftModal from '@/components/SoftModal.vue';

// Un 200 no garantiza que todas las filas entraron: hay que mirar filas_fallidas
const ESTADO_RESULTADO = {
  ok:      { titulo: 'Importación completada', icon: 'fas fa-check', color: 'linear-gradient(135deg,#059669,#10B981)', alerta: 'success' },
  parcial: { titulo: 'Importación completada con errores', icon: 'fas fa-exclamation', color: 'linear-gradient(135deg,#D97706,#F59E0B)', alerta: 'warn' },
  error:   { titulo: 'Error en la importación', icon: 'fas fa-times', color: 'linear-gradient(135deg,#DC2626,#EF4444)', alerta: 'error' },
};

// Campos de `estadisticas` que se muestran si el portal los trae, en este orden
const ESTADISTICA_LABELS = {
  clientes_nuevos: 'Clientes nuevos',
  clientes_actualizados: 'Clientes actualizados',
  contactos_registrados: 'Contactos registrados',
  inmuebles_encontrados: 'Inmuebles encontrados',
  inmuebles_no_encontrados: 'Inmuebles no encontrados',
  codigos_encontrados: 'Códigos encontrados',
  codigos_no_encontrados: 'Códigos no encontrados',
  ciudades_encontradas: 'Ciudades encontradas',
  ciudades_no_encontradas: 'Ciudades no encontradas',
  portales_procesados: 'Portales procesados',
  portales_exitosos: 'Portales exitosos',
  portales_fallidos: 'Portales fallidos',
  vistas_actualizadas: 'Vistas actualizadas',
  links_nuevos: 'Links nuevos',
  links_existentes: 'Links que ya existían',
  tiempo_procesamiento: 'Tiempo',
};

const PORTALES_SECUNDARIOS = {
  properati: 'Properati',
  punto_propiedad: 'Punto Propiedad',
  trovit: 'Trovit',
  mitula: 'Mitula',
  nuroa: 'Nuroa',
  nestoria: 'Nestoria',
};

export default {
  name: 'ImportModule',
  components: { CrmPageHeader, SoftModal },

  setup() {
    const store = useStore();
    const fileInput = ref(null);

    const portalSeleccionado = ref(null);
    const archivoSeleccionado = ref(null);
    const isDragging = ref(false);
    const errorValidacion = ref('');
    const resultadoImportacion = ref(null);
    const showImportModal = ref(false);
    const showResultModal = ref(false);

    const loading = computed(() => store.getters['importaciones/getLoading']);
    const uploadProgress = computed(() => store.getters['importaciones/getUploadProgress']);

    const portales = [
      {
        type: 'inmuebles-links-vistas',
        title: 'Inmuebles',
        description: 'Actualizar links de subportales, vistas y visitas de los inmuebles. No crea leads',
        icon: 'fas fa-home',
        color: 'linear-gradient(135deg,#1E3A5F,#2563EB)',
        example: 'https://res.cloudinary.com/reality-inmo/raw/upload/v1676930127/inmuebles/impordata_ez8ig2.xlsx'
      },
      {
        type: 'contactos-proppit',
        title: 'Contactos Proppit',
        description: 'Importar contactos desde el portal Proppit',
        icon: 'fas fa-users',
        color: 'linear-gradient(135deg,#0284C7,#0EA5E9)',
        example: 'https://res.cloudinary.com/reality-inmo/raw/upload/v1685055183/leads_ejemplo_ugfmat.xlsx'
      },
      {
        type: 'contactos-ciencuadra',
        title: 'Contactos Ciencuadra',
        description: 'Importar contactos desde el portal Ciencuadra',
        icon: 'fas fa-address-book',
        color: 'linear-gradient(135deg,#059669,#10B981)',
        example: 'https://res.cloudinary.com/reality-inmo/raw/upload/v1678380185/inmuebles/ejemplociencuadra_ty9vrw.xlsx'
      },
      {
        type: 'contactos-metrocuadrado',
        title: 'Contactos MetroCuadrado',
        description: 'Importar contactos desde el portal MetroCuadrado',
        icon: 'fas fa-building',
        color: 'linear-gradient(135deg,#D97706,#F59E0B)',
        example: 'https://res.cloudinary.com/reality-inmo/raw/upload/v1684353829/metrocuadrado_flp0ir.xlsx'
      },
      {
        type: 'contactos-fincaraiz',
        title: 'Contactos Fincaraíz',
        description: 'Importar contactos desde el portal de Fincaraíz',
        icon: 'fas fa-map-marked-alt',
        color: 'linear-gradient(135deg,#7C3AED,#8B5CF6)',
        example: 'https://res.cloudinary.com/dgyxzwtxj/raw/upload/v1715788194/documento_ejemplo_fincaraiz_oqdjd3.xlsx'
      }
    ];

    const descargarPlantilla = (portal) => {
      window.open(portal.example, '_blank');
    };

    const abrirModalImportar = (portal) => {
      portalSeleccionado.value = portal;
      limpiarArchivo();
      errorValidacion.value = '';
      showImportModal.value = true;
    };

    const cerrarModalImportar = () => {
      // Mientras procesa, cerrar solo esconderia el cargando: el resultado llega igual
      if (loading.value) return;
      showImportModal.value = false;
    };

    const cerrarModalResultado = () => {
      showResultModal.value = false;
    };

    const handleFileSelect = (event) => {
      const file = event.target.files[0];
      if (file) validarArchivo(file);
    };

    const handleFileDrop = (event) => {
      isDragging.value = false;
      const file = event.dataTransfer.files[0];
      if (file) validarArchivo(file);
    };

    const validarArchivo = (file) => {
      errorValidacion.value = '';
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!['.xlsx', '.xls'].includes(ext)) {
        errorValidacion.value = 'Por favor seleccione un archivo Excel válido (.xlsx o .xls)';
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        errorValidacion.value = 'El archivo es demasiado grande. Tamaño máximo: 10MB';
        return;
      }
      archivoSeleccionado.value = file;
    };

    const limpiarArchivo = () => {
      archivoSeleccionado.value = null;
      errorValidacion.value = '';
      if (fileInput.value) fileInput.value.value = '';
    };

    const procesarImportacion = async () => {
      if (!archivoSeleccionado.value || !portalSeleccionado.value) return;
      try {
        const resultado = await store.dispatch('importaciones/importarArchivo', {
          tipo: portalSeleccionado.value.type,
          file: archivoSeleccionado.value
        });
        resultadoImportacion.value = resultado;
        showImportModal.value = false;
        setTimeout(() => { showResultModal.value = true; }, 250);
        limpiarArchivo();
      } catch (error) {
        console.error('Error en importación:', error);
        const payload = error.response?.data;
        // 422: el motivo concreto viene en errors.file (no es Excel, pesa mas de 10 MB...)
        const errorArchivo = payload?.errors?.file?.[0];
        resultadoImportacion.value = {
          success: false,
          message: errorArchivo || payload?.message || 'Error al procesar la importación'
        };
        showImportModal.value = false;
        setTimeout(() => { showResultModal.value = true; }, 250);
      }
    };

    const estadoResultado = computed(() => {
      const r = resultadoImportacion.value;
      if (!r || !r.success) return 'error';
      return Number(r.data?.filas_fallidas || 0) > 0 ? 'parcial' : 'ok';
    });

    const estadisticas = computed(() => resultadoImportacion.value?.data?.estadisticas || {});

    const erroresResultado = computed(() => resultadoImportacion.value?.data?.errores || []);

    // Inmuebles guarda como maximo 100 errores; total_errores dice cuantos hubo
    const totalErrores = computed(() =>
      Math.max(Number(estadisticas.value.total_errores || 0), erroresResultado.value.length)
    );

    const erroresConPortal = computed(() => erroresResultado.value.some((e) => e.portal));

    const estadisticasVisibles = computed(() =>
      Object.entries(ESTADISTICA_LABELS)
        .filter(([campo]) => estadisticas.value[campo] !== undefined && estadisticas.value[campo] !== null)
        .map(([campo, label]) => ({ campo, label, valor: estadisticas.value[campo] }))
    );

    const nombrePortal = (slug) => PORTALES_SECUNDARIOS[slug] || slug;

    const detallePorPortal = computed(() =>
      Object.entries(estadisticas.value.detalle_por_portal || {}).map(([slug, d]) => ({
        slug,
        nombre: nombrePortal(slug),
        procesados: d?.procesados ?? 0,
        exitosos: d?.exitosos ?? 0,
        fallidos: d?.fallidos ?? 0,
        // Solo Inmuebles: links que el inmueble ya tenia y no se reemplazan
        existentes: d?.existentes,
      }))
    );

    const detalleConExistentes = computed(() =>
      detallePorPortal.value.some((p) => p.existentes !== undefined && p.existentes !== null)
    );

    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    return {
      fileInput,
      portales,
      portalSeleccionado,
      archivoSeleccionado,
      isDragging,
      errorValidacion,
      resultadoImportacion,
      showImportModal,
      showResultModal,
      loading,
      uploadProgress,
      descargarPlantilla,
      abrirModalImportar,
      cerrarModalImportar,
      cerrarModalResultado,
      handleFileSelect,
      handleFileDrop,
      limpiarArchivo,
      procesarImportacion,
      formatFileSize,
      ESTADO_RESULTADO,
      estadoResultado,
      erroresResultado,
      totalErrores,
      erroresConPortal,
      estadisticasVisibles,
      detallePorPortal,
      detalleConExistentes,
      nombrePortal
    };
  }
};
</script>

<style scoped>
/* Upload progress */
.upload-progress-wrap {
  background: #fff;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.upload-progress-label { font-size: .8rem; color: #64748B; }
.upload-progress-pct   { font-size: .8rem; font-weight: 700; color: #1E293B; }
.upload-progress-track { height: 6px; background: #E2E8F0; border-radius: 99px; overflow: hidden; margin-top: 8px; }
.upload-progress-fill  { height: 100%; background: linear-gradient(90deg,#1E3A5F,#2563EB); border-radius: 99px; transition: width .3s ease; }

/* Portal grid */
.import-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

/* Portal card */
.import-card {
  background: #fff;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  transition: all 0.18s ease;
}
.import-card:hover {
  border-color: #BFDBFE;
  box-shadow: 0 6px 20px rgba(37,99,235,0.1);
  transform: translateY(-2px);
}

.import-card__top {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.import-card__icon {
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.1rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.import-card__info { flex: 1; }
.import-card__title { font-size: .88rem; font-weight: 700; color: #1E293B; margin: 0 0 4px; }
.import-card__desc  { font-size: .74rem; color: #64748B; margin: 0; line-height: 1.45; }

.import-card__actions { display: flex; gap: 8px; }

/* Buttons */
.import-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: .78rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
  text-decoration: none;
}

.import-btn--primary {
  background: linear-gradient(135deg,#1E3A5F,#2563EB);
  color: #fff;
  box-shadow: 0 3px 10px rgba(37,99,235,0.25);
  flex: 1;
}
.import-btn--primary:hover:not(:disabled) {
  box-shadow: 0 5px 16px rgba(37,99,235,0.35);
  transform: translateY(-1px);
}
.import-btn--primary:disabled { opacity: .6; cursor: not-allowed; transform: none; }

.import-btn--outline {
  background: transparent;
  color: #475569;
  border: 1.5px solid #E2E8F0;
  flex: 1;
}
.import-btn--outline:hover:not(:disabled) {
  border-color: #2563EB;
  color: #2563EB;
  background: #EFF6FF;
}
.import-btn--outline:disabled { opacity: .6; cursor: not-allowed; }

.w-100 { width: 100%; flex: none; }

/* Modal body */
.modal-import-body { display: flex; flex-direction: column; gap: 12px; }

.modal-tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #EFF6FF;
  border: 1px solid #BFDBFE;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: .78rem;
  color: #1D4ED8;
}
.modal-tip i { margin-top: 1px; flex-shrink: 0; }

.modal-error {
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: .78rem;
  color: #DC2626;
}

.upload-label {
  font-size: .78rem;
  font-weight: 600;
  color: #475569;
  display: block;
  margin-bottom: 6px;
}

/* Dropzone */
.upload-dropzone {
  border: 2px dashed #CBD5E1;
  border-radius: 12px;
  cursor: pointer;
  transition: all .2s ease;
  background: #F8FAFC;
  overflow: hidden;
}
.upload-dropzone:hover,
.upload-dropzone--drag  { border-color: #2563EB; background: #EFF6FF; }
.upload-dropzone--filled { border-color: #10B981; background: #F0FDF4; border-style: solid; }

.upload-dropzone__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 20px;
}
.upload-dropzone__icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: #E2E8F0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: #94A3B8;
  margin-bottom: 10px;
}
.upload-dropzone__text { font-size: .8rem; color: #475569; font-weight: 500; margin: 0 0 4px; }
.upload-dropzone__hint { font-size: .72rem; color: #94A3B8; margin: 0; }

.upload-dropzone__file {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
}
.upload-file-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #DCFCE7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #16A34A;
  flex-shrink: 0;
}
.upload-file-info { flex: 1; min-width: 0; }
.upload-file-name { font-size: .82rem; font-weight: 600; color: #1E293B; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.upload-file-size { font-size: .72rem; color: #64748B; margin: 0; }
.upload-file-remove {
  border: none;
  background: transparent;
  color: #94A3B8;
  cursor: pointer;
  font-size: 1rem;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all .15s;
}
.upload-file-remove:hover { background: #FEF2F2; color: #DC2626; }

/* Result modal */
.result-alert {
  border-radius: 10px;
  padding: 12px 16px;
  font-size: .82rem;
}
.result-alert--success { background: #ECFDF5; color: #065F46; border: 1px solid #A7F3D0; }
.result-alert--error   { background: #FEF2F2; color: #991B1B; border: 1px solid #FECACA; }
.result-alert--warn    { background: #FFFBEB; color: #92400E; border: 1px solid #FDE68A; }

.stats-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 8px; }
.stats-grid__item {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 8px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}
.stats-grid__label { font-size: .72rem; color: #64748B; }
.stats-grid__value { font-size: .78rem; font-weight: 700; color: #1E293B; white-space: nowrap; }

.result-table { border-radius: 8px; border: 1px solid #E2E8F0; }
.result-table th { font-size: .7rem; color: #64748B; padding: 8px 12px; }
.result-table td { font-size: .75rem; padding: 7px 12px; }

.errores-truncados { font-size: .74rem; color: #92400E; margin: 0 0 8px; }

.result-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }

.result-stat {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 16px 12px;
  text-align: center;
}
.result-stat--success { background: #ECFDF5; border-color: #A7F3D0; }
.result-stat--error   { background: #FEF2F2; border-color: #FECACA; }

.result-stat__icon { font-size: 1.2rem; display: block; margin-bottom: 8px; }
.result-stat--success .result-stat__icon { color: #059669; }
.result-stat--error   .result-stat__icon { color: #DC2626; }

.result-stat__value { font-size: 1.4rem; font-weight: 800; color: #1E293B; margin: 0 0 2px; }
.result-stat--success .result-stat__value { color: #059669; }
.result-stat--error   .result-stat__value { color: #DC2626; }

.result-stat__label {
  font-size: .7rem;
  color: #64748B;
  margin: 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .05em;
}

.result-progress-track { height: 8px; background: #E2E8F0; border-radius: 99px; overflow: hidden; }
.result-progress-fill  { height: 100%; border-radius: 99px; transition: width .4s ease; }
.result-progress-fill--success { background: linear-gradient(90deg,#059669,#10B981); }
.result-progress-fill--warn    { background: linear-gradient(90deg,#D97706,#F59E0B); }

.section-mini-label {
  font-size: .7rem;
  font-weight: 700;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 8px;
}
</style>
